import {
  MarketingParams,
  PASSTHROUGH_KEYS,
  TW_MARKETING_PARAMS_GLOBAL,
  TW_REFERRER_KEY,
} from './constants'
import {
  extractMarketingParams,
  extractTwReferrerFromSearch,
  isNewAdArrival,
  mergeMarketingParams,
} from './parse'

/**
 * In-memory snapshot of the marketing params seen this session. The URL is the
 * durable source of truth (URL-only persistence), but this snapshot lets us
 * re-append params to client-side SPA navigations where Next.js produces a clean
 * path, and lets us recover params if a navigation strips them before we react.
 */
let snapshot: MarketingParams = {}

/** Original ad landing URL (origin + pathname), set once per ad arrival. */
let twReferrer: string | null = null

function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

function readEarlyGlobalSearch(): string {
  if (!isBrowser()) {
    return ''
  }

  const globalSearch = ((window as unknown) as Record<string, unknown>)[
    TW_MARKETING_PARAMS_GLOBAL
  ]

  return typeof globalSearch === 'string' ? globalSearch : ''
}

/**
 * Capture marketing params from the current URL (and the early pre-hydration
 * snapshot global, if present). Applies override rules so a later ad arrival
 * replaces the prior platform's attribution, and refreshes `tw_referrer` only
 * when a brand-new ad arrival is detected.
 */
export function captureFromCurrentUrl(): MarketingParams {
  if (!isBrowser()) {
    return {}
  }

  const currentSearch = window.location.search
  const earlySearch = readEarlyGlobalSearch()

  // Prefer the live URL, but fall back to the early-captured search so params
  // aren't lost if something stripped the URL before this ran.
  const fromUrl = extractMarketingParams(currentSearch)
  const fromEarly = extractMarketingParams(earlySearch)
  const incoming = Object.keys(fromUrl).length > 0 ? fromUrl : fromEarly

  const newArrival = isNewAdArrival(snapshot, incoming)

  snapshot = mergeMarketingParams(snapshot, incoming)

  const twReferrerFromUrl =
    extractTwReferrerFromSearch(currentSearch) ??
    extractTwReferrerFromSearch(earlySearch)

  // Determine whether the incoming params are purely passthrough (sfId,
  // sfObjectType, partnershipCode). A passthrough-only arrival should not
  // stamp tw_referrer because it is not an ad landing.
  const incomingKeys = Object.keys(incoming)
  const isPassthroughOnlyArrival =
    incomingKeys.length > 0 &&
    incomingKeys.every((key) =>
      PASSTHROUGH_KEYS.includes(key as typeof PASSTHROUGH_KEYS[number])
    )

  // tw_referrer reflects the latest ad landing. Prefer an explicit URL value
  // (e.g. from WordPress); otherwise set on first ad arrival and refresh on a
  // brand-new ad arrival when no URL value is present.
  if (twReferrerFromUrl) {
    twReferrer = twReferrerFromUrl
  } else if (
    Object.keys(incoming).length > 0 &&
    !isPassthroughOnlyArrival &&
    (newArrival || !twReferrer)
  ) {
    twReferrer = `${window.location.origin}${window.location.pathname}`
  }

  return { ...snapshot }
}

export function getMarketingParams(): MarketingParams {
  return { ...snapshot }
}

export function getTwReferrer(): string | null {
  return twReferrer
}

export function hasMarketingParams(): boolean {
  return Object.keys(snapshot).length > 0
}

/**
 * Snapshot including the tw_referrer key, used when re-appending params to the
 * URL. tw_referrer is only included once it has been established.
 */
export function getPersistedParams(): MarketingParams & {
  [TW_REFERRER_KEY]?: string
} {
  const params: MarketingParams & { [TW_REFERRER_KEY]?: string } = {
    ...snapshot,
  }

  if (twReferrer) {
    params[TW_REFERRER_KEY] = twReferrer
  }

  return params
}

export function clear(): void {
  snapshot = {}
  twReferrer = null

  // Drop the pre-hydration snapshot too. Otherwise the next SPA navigation with
  // a clean URL falls back to a stale __twMarketingParams and undoes the flush.
  if (isBrowser()) {
    delete ((window as unknown) as Record<string, unknown>)[
      TW_MARKETING_PARAMS_GLOBAL
    ]
  }
}

/** Test-only reset helper. */
export function __resetForTests(): void {
  clear()
}
